import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { FileIcon, UploadIcon, XIcon } from "lucide-react";
import { useCallback, useRef, useState } from "react";

interface DialogTeamKnowledgeFileUploadProps {
  open: boolean;
  onClose: () => void;
  onUpload: (files: File[]) => void;
  isUploading: boolean;
}

export default function DialogTeamKnowledgeFileUpload({
  open,
  onClose,
  onUpload,
  isUploading,
}: DialogTeamKnowledgeFileUploadProps) {
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const FILES_UPLOAD_EXTENSIONS_ACCEPTED = [
    "txt",
    "pdf",
    "csv",
    "xls",
    "xlsx",
    "doc",
    "docx",
    "odt",
    "odp",
    "ods",
    "ppt",
    "pptx",
  ];

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    setSelectedFiles((prev) => [...prev, ...fileArray]);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (selectedFiles.length > 0) {
      onUpload(selectedFiles);
      setSelectedFiles([]);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t("team.knowledge.dialog.upload.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <div
              className={cn(
                "p-8 text-center rounded-lg border-2 border-dashed transition-colors",
                isDragOver
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25",
                "hover:border-primary/50 hover:bg-primary/5"
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <UploadIcon className="mx-auto mb-4 w-12 h-12 text-muted-foreground" />

              <Button
                onClick={handleButtonClick}
                className="mb-4"
                disabled={isUploading}
              >
                <FileIcon className="mr-2 w-4 h-4" />
                {t("team.knowledge.dialog.upload.selectFile")}
              </Button>

              <p className="text-sm text-muted-foreground">
                {t("team.knowledge.dialog.upload.dragDropText")}
              </p>

              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple
                accept={FILES_UPLOAD_EXTENSIONS_ACCEPTED.map(
                  (ext) => `.${ext}`
                ).join(",")}
                onChange={handleInputChange}
              />
            </div>

            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">
                  {t("team.knowledge.dialog.upload.selectedFiles")}
                </h4>
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center p-2 rounded bg-muted"
                  >
                    <div className="flex items-center">
                      <FileIcon className="mr-2 w-4 h-4 text-muted-foreground" />
                      <span className="text-sm truncate">
                        {file.name.length > 35
                          ? file.name.slice(0, 35) + "..."
                          : file.name}
                      </span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                      className="p-0 w-6 h-6"
                    >
                      <XIcon className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start">
                <span className="inline-block shrink-0 mt-2 mr-3 w-2 h-2 bg-current rounded-full" />
                {t("team.knowledge.dialog.upload.info.textExtraction")}
              </li>
              <li className="flex items-start">
                <span className="inline-block shrink-0 mt-2 mr-3 w-2 h-2 bg-current rounded-full" />
                {t("team.knowledge.dialog.upload.info.supportedFormats", {
                  maxSize: 50,
                  formats: FILES_UPLOAD_EXTENSIONS_ACCEPTED.map((ext) =>
                    ext.toUpperCase()
                  ).join(", "),
                })}
              </li>
              <li className="flex items-start">
                <span className="inline-block shrink-0 mt-2 mr-3 w-2 h-2 bg-current rounded-full" />
                {t("team.knowledge.dialog.upload.info.limitations")}
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isUploading}
            >
              {t("team.knowledge.dialog.upload.cancel")}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={selectedFiles.length === 0 || isUploading}
            >
              {t("team.knowledge.dialog.upload.upload")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
